import { CSSProperties, useEffect, useState } from 'react';
import Colors from 'modules/Colors';
import { ReactComponent as CheckGreen } from 'assets/images/check-green.svg';
import useScreen from 'Hooks/useScreen';
import { BillingCycle } from 'components/shareable/UpgradeToProModal/utils';
import {
    PlanType,
    DEFAULT_CURRENCY,
    Perk
} from 'modules/Const';
import { CurrencyCodesType } from 'modules/Types';
import { getConvertedCurrency_api } from 'api/campaignApi';
import { getNumberWithDecimals } from 'helpers/General';
import getSymbolFromCurrency from 'currency-symbol-map';
import { formatNumberToCurrency, styx } from 'modules/Utils';
import ButtonSymphony from 'elements/ButtonSymphony';
import Icon from 'elements/Icon';
import usePlans from 'Hooks/usePlans';
import { useAuth0 } from '@auth0/auth0-react';

interface PublicPricingTileProps {
    planType: PlanType;
    billingCycle: BillingCycle;
    currencyCode: string;
    isMostPopular?: boolean;
    onGetStarted: () => void;
    isLoading?: boolean;
}

const PublicPricingTile = ({
    planType,
    billingCycle,
    currencyCode,
    isMostPopular = false,
    onGetStarted,
    isLoading = false,
}: PublicPricingTileProps) => {
    const { mobileView, mStyle } = useScreen();
    const { isAuthenticated, loginWithRedirect } = useAuth0();
    const {
        plans,
        isLoading: plansLoading,
        error: plansError,
        getPlan,
        isInitialized,
        generatePerksFromPlan
    } = usePlans();

    const [userFacingPrice, setUserFacingPrice] = useState<number>(0);

    const plan = getPlan(planType);

    /** Gets the base price for a subscription plan from dynamic data */
    const getBasePrice = (cycle: BillingCycle): number => {
        if (!plan) return 0;

        return cycle === BillingCycle.MONTHLY
            ? plan.pricing.monthly.cents / 100
            : plan.pricing.annual.cents / 100;
    };

    /** Base price from dynamic data */
    const basePrice = getBasePrice(billingCycle);

    /**
     * Converts base price to user's selected currency
     */
    const convertToNewCurrencyPrice = async (): Promise<void> => {
        if (!plan) {
            setUserFacingPrice(0);
            return;
        }

        // If currency matches the plan's currency, use the price directly
        if (currencyCode === plan.pricing.monthly.currency) {
            setUserFacingPrice(basePrice);
            return;
        }

        // Otherwise, convert currency
        if (currencyCode !== DEFAULT_CURRENCY) {
            try {
                const res = await getConvertedCurrency_api({
                    from: DEFAULT_CURRENCY,
                    to: currencyCode as CurrencyCodesType,
                    amount: basePrice
                });
                setUserFacingPrice(Number(getNumberWithDecimals(res, 2)));
            } catch (error) {
                console.error('Error converting currency:', error);
                setUserFacingPrice(basePrice);
            }
        } else {
            setUserFacingPrice(basePrice);
        }
    };

    useEffect(() => {
        if (plan) {
            convertToNewCurrencyPrice();
        }
    }, [currencyCode, billingCycle, basePrice, plan]);

    // Loading state
    if (plansLoading) {
        return (
            <div className="publicPricingTileComponent" style={sty.publicPricingTileComponent}>
                <div className="publicPricingTileInner" style={sty.publicPricingTileInner}>
                    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                        <Icon className="loadingIcon" icon="loading" size={24} color={Colors.purple} />
                        <div style={{ marginTop: 12, color: Colors.greyDark }}>Loading pricing...</div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (plansError || !plan) {
        return (
            <div className="publicPricingTileComponent" style={sty.publicPricingTileComponent}>
                <div className="publicPricingTileInner" style={sty.publicPricingTileInner}>
                    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                        <Icon className="warningIcon" icon="warning" size={24} color={Colors.red} />
                        <div style={{ marginTop: 12, color: Colors.greyDark }}>
                            Unable to load pricing. Please try again.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Create plan data structure similar to SUBSCRIPTION_BENEFITS for backward compatibility
    const planData = {
        title: plan.planType === PlanType.STARTER ? 'Starter' :
            plan.planType === PlanType.GROWTH ? 'Growth' :
                plan.planType === PlanType.TEAM_V2 ? 'Team' : 'Unknown',
        subtitle: plan.planType === PlanType.STARTER ? 'Essential tools for launching and growing your brand.' :
            plan.planType === PlanType.GROWTH ? 'Advanced features for creators ready to scale their marketing.' :
                plan.planType === PlanType.TEAM_V2 ? 'Collaborative tools for teams managing multiple brands.' : '',
        buttonText: plan.planType === PlanType.STARTER ? 'Get Started' :
            plan.planType === PlanType.GROWTH ? 'Get Started' :
                plan.planType === PlanType.TEAM_V2 ? 'Get Started' : 'Get Started',
        perks: generatePerksFromPlan(planType, plan),
        valueComparison: plan.planType === PlanType.STARTER ? [
            { name: 'Linktree Pro', price: 5 },
            { name: 'Wix Basic', price: 14 },
            { name: 'Later Starter', price: 15 },
            { name: 'Google Analytics Pro', price: 10 },
        ] : plan.planType === PlanType.GROWTH ? [
            { name: 'ChatGPT Plus', price: 20 },
            { name: 'Feature.fm', price: 20 },
            { name: 'Sprout Social', price: 25 },
            { name: 'Laylo', price: 20 },
        ] : plan.planType === PlanType.TEAM_V2 ? [
            { name: 'Hootsuite Business', price: 99 },
            { name: 'Sprout Social Advanced', price: 149 },
            { name: 'Asana Business', price: 24 },
            { name: 'Advanced Analytics', price: 50 },
            { name: 'Client Collaboration', price: 30 },
        ] : []
    };



    const renderSubscriptionIcon = () => {
        const iconProps = {
            className: 'subscriptionIcon',
            style: sty.subscriptionIcon,
            icon: 'gem' as const,
            size: 38,
            isCircle: true,
        };

        switch (planType) {
            case PlanType.STARTER:
                return (
                    <Icon
                        {...iconProps}
                        color={Colors.blue}
                        style={{
                            ...sty.subscriptionIcon,
                            backgroundColor: 'rgba(0, 163, 255, 0.16)',
                        }}
                    />
                );
            case PlanType.GROWTH:
                return (
                    <Icon
                        {...iconProps}
                        color={Colors.purple}
                    />
                );
            case PlanType.TEAM_V2:
                return (
                    <Icon
                        {...iconProps}
                        color={Colors.gold}
                        style={{
                            ...sty.subscriptionIcon,
                            backgroundColor: Colors.goldTransparent,
                        }}
                    />
                );
            default:
                return null;
        }
    };

    const renderTitle = () => {
        return (
            <div className="tileTitle" style={sty.tileTitle}>
                {renderSubscriptionIcon()}
                {planData.title}
            </div>
        );
    };

    const renderPrice = () => {
        const currencySymbol = getSymbolFromCurrency(currencyCode);
        const isAnnual = billingCycle === BillingCycle.ANNUAL;

        // Calculate the display price for annual billing with proper decimal handling
        const displayPrice = isAnnual ?
            formatNumberToCurrency(userFacingPrice / 12, true) :
            formatNumberToCurrency(userFacingPrice, true);

        return (
            <>
                <div className="tilePriceContainer" style={sty.tilePriceContainer}>
                    <div className="tilePriceWrapper" style={sty.tilePriceWrapper}>
                        <div className="tilePrice" style={sty.tilePrice}>

                            {currencySymbol}
                            {displayPrice}
                        </div>
                    </div>
                    <div className="tilePricePer" style={sty.tilePricePer}>
                        / month
                    </div>
                </div>

                {isAnnual && (
                    <div className="tilePriceAnnual" style={sty.tilePriceAnnual}>
                        <div 
                        className="tilePriceAnnualWrapper"
                        style={sty.tilePriceAnnualWrapper}>
                            {currencySymbol}
                            {formatNumberToCurrency(userFacingPrice, true)}
                        </div>
                        <div style={sty.billingNote}>
                            / year billed upfront
                        </div>
                    </div>
                )}
            </>
        );
    };

    const renderSubtitle = () => {
        return (
            <div className="tileSubtitle" style={sty.tileSubtitle}>
                {planData.subtitle}
            </div>
        );
    };

    const renderMostPopularBanner = () => {
        if (!isMostPopular) {
            return null;
        }

        return (
            <div
                className="mostPopularBanner"
                style={sty.mostPopularBanner}
            >
                MOST POPULAR
            </div>
        );
    };

    const renderActionButton = () => {
        // Handle Team plan with Contact Us
        if (planType === PlanType.TEAM_V2) {
            return (
                <ButtonSymphony
                    className="contactUsButton"
                    width="100%"
                    onClick={() => {
                        window.location.href = 'mailto:megh@symphonyos.co?subject=Team Plan Inquiry';
                    }}
                >
                    Contact Us
                </ButtonSymphony>
            );
        }

        // Handle authentication for other plans
        const handleButtonClick = () => {
            if (isAuthenticated) {
                // If authenticated, use the provided onGetStarted callback
                onGetStarted();
            } else {
                // If not authenticated, redirect to login with return to current page
                loginWithRedirect({
                    appState: {
                        returnTo: window.location.pathname + window.location.search
                    }
                });
            }
        };

        return (
            <ButtonSymphony
                className="getStartedButton"
                isLoading={isLoading}
                disabled={isLoading}
                width="100%"
                onClick={handleButtonClick}
            >
                {planData.buttonText}
            </ButtonSymphony>
        );
    };

    const renderDetails = () => {
        const perks = planData.perks;

        return (
            <div className="tileDetails" style={sty.tileDetails}>
                {perks.map((perk: Perk, index: number) => {
                    const { icon, numeric, label, subtitle } = perk;
                    const hasMobileStyles = mobileView ? {
                        flexDirection: 'column' as const,
                        alignItems: 'flex-start' as const
                    } : {};

                    return (
                        <div
                            key={`perk-${index}`}
                            className="perkContainer"
                            style={{
                                ...sty.perkContainer,
                                ...hasMobileStyles
                            }}
                        >
                            <div className="perkInfoContainer" style={sty.perkInfoContainer}>
                                <Icon
                                    className="perkIcon"
                                    icon={icon}
                                    size={20}
                                    color={Colors.green}
                                    style={{ marginRight: 12, flexShrink: 0 }}
                                />
                                <div className="perkMainContent" style={sty.perkMainContent}>
                                    <div className="perkLabelContainer" style={sty.perkLabelContainer}>
                                        {numeric && (
                                            <span className="perkNumeric" style={sty.perkNumeric}>
                                                {numeric}
                                            </span>
                                        )}
                                        <span className="perkLabel" style={sty.perkLabel}>
                                            {label}
                                        </span>
                                    </div>
                                    {subtitle && (
                                        <div className="perkSubtitle" style={sty.perkSubtitle}>
                                            {subtitle}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Get tier-specific background color
    const getTierBackgroundColor = () => {
        switch (planType) {
            case PlanType.STARTER:
                return 'rgba(0, 163, 255, 0.08)'; // Light blue
            case PlanType.GROWTH:
                return 'rgba(99, 66, 194, 0.08)'; // Light purple
            case PlanType.TEAM_V2:
                return 'rgba(255, 193, 7, 0.08)'; // Light gold
            default:
                return 'transparent';
        }
    };

    // Conditionally apply styles for mobile view
    const mobileStyles = mobileView ? {
        maxWidth: '100%',
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)'
    } : {};

    return (
        <div
            className="publicPricingTileComponent"
            style={styx(
                sty.publicPricingTileComponent,
                isMostPopular ? sty.highlightedTile : {},
                mobileStyles
            )}
        >
            {renderMostPopularBanner()}
            <div
                className="publicPricingTileInner"
                style={{
                    ...sty.publicPricingTileInner,
                    paddingTop: 48,
                    backgroundColor: getTierBackgroundColor()
                }}
            >
                {renderTitle()}
                {renderPrice()}
                {renderSubtitle()}
                {renderActionButton()}
                {/* {renderValueComparison()} */}
            </div>

            <div style={{ backgroundColor: getTierBackgroundColor() }}>
                {renderDetails()}
            </div>
        </div>
    );
};

/** Component styles */
const sty: Record<string, CSSProperties> = {
    publicPricingTileComponent: {
        width: '100%',
        maxWidth: 350,
        border: `1px solid ${Colors.DARK_DIVIDER_LINE}`,
        borderRadius: 16,
        backgroundColor: 'white',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        position: 'relative',
        overflow: 'hidden',
    },
    publicPricingTileInner: {
        paddingTop: 28,
        paddingLeft: 16,
        paddingRight: 16,
        paddingBottom: 16,
        borderBottom: `1px solid ${Colors.DARK_DIVIDER_LINE}`,
    },
    tileTitle: {
        display: 'flex',
        flexDirection: 'row',
        fontSize: 24,
        alignItems: 'flex-end',
        fontWeight: 500,
        marginBottom: 16,
    },
    tilePriceContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        marginBottom: 8,
    },
    tilePriceWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 8
    },
    tilePrice: {
        fontSize: 40,
        fontWeight: 500,
    },
    tilePricePer: {
        alignSelf: 'center',
        color: Colors.greyDark,
    },
    tilePriceAnnual: {
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        alignItems: 'center',
        fontSize: 16,
        fontWeight: 400,
        color: Colors.greyDark,
        marginBottom: 8,
    },
    tilePriceAnnualWrapper: {
       fontWeight: 600
    },
    tileSubtitle: {
        fontSize: 16,
        color: Colors.black,
        minHeight: 60,
        lineHeight: 1.4,
    },
    mostPopularBanner: {
        textAlign: 'center',
        width: '100%',
        backgroundColor: Colors.purpleLightest,
        color: Colors.purple,
        padding: '8px 16px',
        borderTopRightRadius: 16,
        borderTopLeftRadius: 16,
        fontSize: 12,
        fontWeight: 600,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
    },
    tileDetails: {
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
        gap: 16,
    },
    perkContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    perkInfoContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        flex: 1,
    },
    perkMainContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        flex: 1,
    },
    perkLabelContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    perkNumeric: {
        fontSize: 14,
        fontWeight: 600,
        color: Colors.textColor,
    },
    perkLabel: {
        color: Colors.greyDark,
        fontSize: 14,
        lineHeight: 1.4,
    },
    perkSubtitle: {
        fontSize: 12,
        color: Colors.green,
        fontWeight: 500,
    },
    subscriptionIcon: {
        flexShrink: 0,
        marginRight: 8,
        borderRadius: 12,
        padding: 7
    },
    billingNote: {
        color: Colors.greyDark,
        fontSize: 12
    },
    highlightedTile: {
        border: `2px solid ${Colors.purple}`,
        boxShadow: `0 0 12px rgba(99, 66, 194, 0.25)`,
    },
    valueComparison: {
        backgroundColor: 'rgba(255, 248, 225, 1)',
        padding: 20,
        borderRadius: 12,
        marginBottom: 24,
        border: `1px solid rgba(255, 193, 7, 0.3)`,
        boxShadow: '0px 2px 6px rgba(255, 193, 7, 0.1)',
    },
    valueComparisonTitle: {
        fontSize: 15,
        fontWeight: 600,
        color: Colors.textColor,
        marginBottom: 16,
        textAlign: 'center',
    },
    competitorList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        marginBottom: 16,
    },
    competitorItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: 14,
        color: Colors.textColor,
        backgroundColor: 'white',
        padding: '8px 12px',
        borderRadius: 6,
        border: '1px solid rgba(255, 193, 7, 0.2)',
    },
    competitorPrice: {
        fontWeight: 600,
        color: Colors.textColor,
    },
    totalSavings: {
        borderTop: `2px solid rgba(255, 193, 7, 0.4)`,
        paddingTop: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        textAlign: 'center',
    },
    totalSavingsText: {
        fontSize: 15,
        color: Colors.textColor,
    },
    totalSavingsAmount: {
        fontWeight: 700,
    },
    savingsHighlight: {
        fontSize: 15,
        fontWeight: 700,
        color: Colors.green,
    },
    tileCurrencySymbol: {
        fontSize: 40,
        fontWeight: 500,
    },
    tileCurrencySymbol_mobile: {
        fontSize: 24,
        fontWeight: 500,
    },

};

export default PublicPricingTile; 